// generated from server/src/shared-clip.ts by npm run shared-clip -w server; never edit
/**
 * The rules for a voice message shared to Eaves, one source (no imports): `npm run shared-clip -w server` copies
 * this file verbatim to web/src/shared-clip.generated.ts, so the screen and the server refuse the same clips.
 */

/** A shared voice message is at most this long (seconds). */
export const SHARED_MAX_S = 30;
/** Slack for a length measured a little long (encoders pad the end). */
export const SHARED_SLACK_S = 0.5;
/** 30 s of a chat app's Opus is about 100 KB; this is far past that and still small. */
export const SHARED_MAX_BYTES = 2_000_000;
/** A clip kept on the device for the page waits at most this long. */
export const SHARED_STALE_MS = 15 * 60_000;

/**
 * The type of a clip, read from its bytes, never from the request: the provider takes a wrong or parameterised
 * type as text and the model then invents a scene, and it ignores MP3. '' for anything else.
 */
export function sniffAudioMime(head: Uint8Array): string {
  const at = (o: number, t: string) => [...t].every((ch, i) => head[o + i] === ch.charCodeAt(0));
  if (head[0] === 0x1a && head[1] === 0x45 && head[2] === 0xdf && head[3] === 0xa3) return 'audio/webm';
  if (at(0, 'OggS')) return 'audio/ogg';
  if (at(4, 'ftyp')) return 'audio/mp4';
  if (at(0, 'RIFF') && at(8, 'WAVE')) return 'audio/wav';
  if (at(0, 'fLaC')) return 'audio/flac';
  return '';
}

/**
 * The length in seconds of an Ogg Opus file (a chat app's voice note), from its pages alone: the granule position
 * of the last page that has one, less the pre-skip, at 48 kHz. null for anything else or a malformed file.
 * No CRC check: this reads a length, it does not vouch for the bytes.
 */
export function oggOpusSeconds(bytes: Uint8Array): number | null {
  const at = (o: number, t: string) => o >= 0 && o + t.length <= bytes.length && [...t].every((ch, i) => bytes[o + i] === ch.charCodeAt(0));
  if (!at(0, 'OggS') || bytes.length < 27) return null;
  const segs = bytes[26];
  const first = 27 + segs;
  if (first + 19 > bytes.length || !at(first, 'OpusHead')) return null;
  const preSkip = bytes[first + 10] | (bytes[first + 11] << 8);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  // From the end, the last capture pattern that opens a page with a granule position (-1: none, look before it).
  for (let o = bytes.length - 27; o >= 0; o--) {
    // 'O' first: a 2 MB scan must not build a string per byte.
    if (bytes[o] !== 0x4f || !at(o, 'OggS')) continue;
    const granule = view.getBigInt64(o + 6, true);
    if (granule < 0n) continue;
    const samples = Number(granule) - preSkip;
    return samples >= 0 ? samples / 48000 : null;
  }
  return null;
}

/** Why a shared clip is turned away, before anything is made: its type is not audio we read, or it is too long. */
export function sharedRefusal(o: { bytes: number; mime: string | null; seconds: number | null }): 'unsupported' | 'too-long' | null {
  if (o.mime === null) return 'unsupported';
  if (o.bytes > SHARED_MAX_BYTES || (o.seconds !== null && o.seconds > SHARED_MAX_S + SHARED_SLACK_S)) return 'too-long';
  return null;
}
